import { i18nTranslationHelper } from '@/i18n'
import { probeReachabilityOnce, Reachability } from '@/infra/http/connectivity-probe'
import { NetworkError } from '@/infra/http/network-error'
import { getErrorCodeForReachability } from './error-code-catalog'

/**
 * Type guard para `NetworkError`. Centraliza o import e dá um ponto único para
 * os handlers de erro decidirem se delegam ao snackbar global (caminho React
 * Query) ou exibem a mensagem amigável (chamada direta) — ver
 * `getImmediateErrorMessage`.
 */
export const isNetworkError = (error: unknown): error is NetworkError =>
  error instanceof NetworkError

export type ResolveErrorMessageOptions = {
  /**
   * Override do GET de reachability. Por padrão usa a URL canônica
   * (ver connectivity-probe); só informe se um fluxo precisar sondar outro
   * endpoint.
   */
  probeUrl?: string
}

/**
 * Prefixa a mensagem com o código de erro entre colchetes — é ele que a pessoa
 * informa ao suporte (ver error-code-catalog). Quando há `correlationId`, a
 * referência técnica vai no fim: ela não é para a pessoa ler, só para o suporte
 * achar a ocorrência no Sentry, então não disputa a abertura da mensagem.
 */
export const withErrorCode = (message: string, code: string, correlationId?: string): string => {
  const prefix = i18nTranslationHelper.getTComponents('errorCode.prefix', { code })
  const reference = correlationId
    ? ` ${i18nTranslationHelper.getTComponents('errorCode.reference', { correlationId })}`
    : ''

  return `${prefix} ${message}${reference}`
}

const messageForReachability = (verdict: Reachability, correlationId?: string): string => {
  const code = getErrorCodeForReachability(verdict)

  switch (verdict) {
    case 'offline':
      return withErrorCode(
        i18nTranslationHelper.getTComponents('networkErrorSnackbar.offline'),
        code
      )
    case 'reachable':
      return withErrorCode(
        i18nTranslationHelper.getTComponents('networkErrorSnackbar.proxyBlocked'),
        code,
        correlationId
      )
    case 'unreachable':
    default:
      return withErrorCode(
        i18nTranslationHelper.getTComponents('networkErrorSnackbar.unreachable'),
        code
      )
  }
}

/**
 * Mensagem síncrona imediata, sem esperar o probe. Para erro de rede dá um
 * palpite neutro (offline / inalcançável) que pode ser refinado por
 * `resolveErrorMessage` (ex.: proxyBlocked). Use para feedback instantâneo
 * antes do `await`.
 *
 * Também já leva código: sem ele a mensagem imediata apareceria "pelada" e o
 * código surgiria do nada ~3s depois, quando o probe conclui.
 */
export function getImmediateErrorMessage(error: unknown): string {
  if (!(error instanceof NetworkError)) {
    return error instanceof Error ? error.message : String(error)
  }
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
  return messageForReachability(isOffline ? 'offline' : 'unreachable', error.correlationId)
}

/**
 * Resolve a mensagem a ser exibida para um erro qualquer.
 *
 * - Não-NetworkError → usa a própria mensagem do erro.
 * - NetworkError → roda o probe de conectividade (memoizado por erro, então
 *   compartilhado com o report do Sentry) e escolhe a mensagem adequada
 *   (offline / bloqueio de rede-proxy / servidor inalcançável).
 *
 * É async por causa do probe; mantenha o `await` no chamador.
 */
export async function resolveErrorMessage(
  error: unknown,
  options: ResolveErrorMessageOptions = {}
): Promise<string> {
  if (!(error instanceof NetworkError)) {
    return error instanceof Error ? error.message : String(error)
  }

  const verdict = await probeReachabilityOnce(error, options.probeUrl)
  return messageForReachability(verdict, error.correlationId)
}
